import { useState, useRef, useEffect } from "react";
import { useBrand } from "@/lib/brand-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  MessageCircle, 
  X, 
  Send, 
  Bot, 
  User,
  Loader2,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface QuickAction {
  label: string;
  query: string;
}

export function AIChatWidget() {
  const { isShiftCanvas } = useBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const primaryColor = isShiftCanvas ? "#4ECDC4" : "#f97316";
  const brandName = isShiftCanvas ? "ShiftCanvas" : "Centretable";

  const quickActions: QuickAction[] = isShiftCanvas ? [
    { label: "How do I create a roster?", query: "How do I create my first roster?" },
    { label: "Add staff members", query: "How do I add staff to my organization?" },
    { label: "Set up clock-in", query: "How do I set up clock in/out for my team?" },
    { label: "Shift swap process", query: "How does the shift swap process work?" },
  ] : [
    { label: "Add products", query: "How do I add products to my POS?" },
    { label: "Set up payments", query: "How do I set up EFTPOS integration?" },
    { label: "Customer ordering", query: "How do I enable customer ordering?" },
    { label: "Export to Xero", query: "How do I export sales to Xero?" },
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const generateResponse = async (query: string, history: Message[]): Promise<string> => {
    try {
      const conversationHistory = history.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await fetch("/api/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: query,
          history: conversationHistory,
          brand: isShiftCanvas ? "shiftcanvas" : "centretable"
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }
      
      const data = await response.json();
      return data.response;
    } catch (error) {
      console.error("AI chat error:", error);
      return `I apologize, I'm having trouble responding right now. For immediate help:\n\n- Check our **[FAQ](/faq)** for quick answers\n- Browse the **[Setup Guides](/guides)** for step-by-step tutorials\n- **[Contact Support](/support)** if you need personalized help`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await generateResponse(userMessage.content, updatedMessages);
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response,
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I'm sorry, I encountered an error. Please try again or contact our support team for assistance.",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (query: string) => {
    setInput(query);
    inputRef.current?.focus();
  };

  const renderContent = (content: string) => {
    const parts = content.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      
      const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        return (
          <a 
            key={index}
            href={linkMatch[2]}
            className="underline hover:no-underline"
            style={{ color: primaryColor }}
          >
            {linkMatch[1]}
          </a>
        );
      }
      
      return part;
    });
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed bottom-20 right-4 z-50 flex flex-col bg-background border rounded-xl shadow-xl w-[360px] h-[500px] max-h-[70vh]"
              data-testid="ai-chat-panel"
            >
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    <Sparkles className="h-5 w-5" style={{ color: primaryColor }} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">{brandName} Assistant</h2>
                    <p className="text-sm text-muted-foreground">AI-powered help</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  data-testid="button-close-chat"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="flex-1 overflow-hidden">
                <ScrollArea 
                  className="h-full px-4"
                  ref={scrollRef}
                >
                  {messages.length === 0 ? (
                    <div className="py-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        Hi! I'm here to help you get started with {brandName}. Ask me anything or try one of these:
                      </p>
                      <div className="space-y-2">
                        {quickActions.map((action, index) => (
                          <button
                            key={index}
                            onClick={() => handleQuickAction(action.query)}
                            className="w-full text-left p-2 rounded-lg text-sm hover:bg-muted transition-colors flex items-center justify-between group"
                            data-testid={`button-quick-action-${index}`}
                          >
                            <span>{action.label}</span>
                            <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="py-4 space-y-4">
                      {messages.map((message) => (
                        <div 
                          key={message.id}
                          className={`flex gap-2 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                          data-testid={`message-${message.role}-${message.id}`}
                        >
                          <div 
                            className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                              message.role === "user" 
                                ? "bg-muted" 
                                : ""
                            }`}
                            style={message.role === "assistant" ? { backgroundColor: `${primaryColor}15` } : {}}
                          >
                            {message.role === "user" ? (
                              <User className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Bot className="h-4 w-4" style={{ color: primaryColor }} />
                            )}
                          </div>
                          <div 
                            className={`rounded-lg p-3 text-sm max-w-[85%] ${
                              message.role === "user" 
                                ? "bg-muted" 
                                : "bg-background border"
                            }`}
                          >
                            <div className="whitespace-pre-wrap leading-relaxed">
                              {renderContent(message.content)}
                            </div>
                          </div>
                        </div>
                      ))}
                      {isLoading && (
                        <div className="flex gap-2">
                          <div 
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${primaryColor}15` }}
                          >
                            <Bot className="h-4 w-4" style={{ color: primaryColor }} />
                          </div>
                          <div className="rounded-lg p-3 bg-background border">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </div>

              <div className="p-4 border-t">
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask a question..."
                    disabled={isLoading}
                    className="flex-1"
                    data-testid="input-chat-message"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    disabled={!input.trim() || isLoading}
                    style={{ backgroundColor: primaryColor }}
                    data-testid="button-send-message"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white"
        style={{ backgroundColor: primaryColor }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="button-chat-toggle"
        aria-label={isOpen ? "Close chat" : "Open chat assistant"}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="h-6 w-6" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <MessageCircle className="h-6 w-6" />
            </motion.div>
          )}
        </AnimatePresence>
        
        {!isOpen && messages.length === 0 && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
        )}
      </motion.button>
    </>
  );
}
